import {createContext, useContext, useEffect, useState} from 'react';
import bridge, {AnyReceiveMethodName, AppearanceType, ParentConfigData, VKBridgeEvent} from '@vkontakte/vk-bridge';
import {Appearance} from '@vkontakte/vkui';
import {useSearchBaseParams} from './searchParamsContext';

// Тут описана схема выставления темы https://confluence.vk.team/pages/viewpage.action?pageId=1573502557
const AppearanceContext = createContext<AppearanceType | undefined>(undefined);

type AppearanceContextProps = {
    children: React.ReactNode;
    initialTheme?: AppearanceType;
};

const getInitialAppearance = ({
    initialTheme,
    isDarkTheme,
}: {
    initialTheme?: AppearanceType;
    isDarkTheme: string | null;
}) => {
    if (initialTheme) {
        return initialTheme;
    } else if (isDarkTheme) {
        return isDarkTheme === '1' ? Appearance.DARK : Appearance.LIGHT;
    } else if (window.matchMedia(`(prefers-color-scheme: ${Appearance.DARK})`).matches) {
        return Appearance.DARK;
    }

    return Appearance.LIGHT;
};

export const AppearanceContextProvider: React.FC<AppearanceContextProps> = ({children, initialTheme}) => {
    const {isDarkThemeParam, isVkClient, isOkClient} = useSearchBaseParams();

    // смена темы через бридж VKWebAppUpdateConfig поддержаны только ВК и ОК
    const isSupportedBridgeClient = isVkClient || isOkClient;

    const [appearance, setAppearance] = useState<AppearanceType>(
        getInitialAppearance({initialTheme, isDarkTheme: isDarkThemeParam}),
    );

    useEffect(() => {
        if (!isSupportedBridgeClient) {
            if (isDarkThemeParam) {
                setAppearance(isDarkThemeParam === '1' ? Appearance.DARK : Appearance.LIGHT);
                return;
            }

            const listenScheme = (event: MediaQueryListEvent) => {
                setAppearance(event.matches ? Appearance.DARK : Appearance.LIGHT);
            };
            const mediaQuery = window.matchMedia(`(prefers-color-scheme: ${Appearance.DARK})`);

            if (mediaQuery.addEventListener) {
                mediaQuery.addEventListener('change', listenScheme);
            }

            return () => {
                if (mediaQuery.removeEventListener) {
                    mediaQuery.removeEventListener('change', listenScheme);
                }
            };
        }
        return;
    }, [isDarkThemeParam, isSupportedBridgeClient]);

    // подписываемся на бридж обновление темы
    useEffect(() => {
        // через бридж обновляем тему только для ВК и ОК
        if (!isSupportedBridgeClient) return;
        const listenAppearence = ({detail: {data, type}}: VKBridgeEvent<AnyReceiveMethodName>) => {
            if (type === 'VKWebAppUpdateConfig') {
                setAppearance((data as ParentConfigData).appearance);
            }
        };
        bridge.subscribe(listenAppearence);

        return () => {
            bridge.unsubscribe(listenAppearence);
        };
    }, [isSupportedBridgeClient]);

    return <AppearanceContext.Provider value={appearance}>{children}</AppearanceContext.Provider>;
};

export const useAppearance = () => {
    const appearance = useContext(AppearanceContext);
    if (!appearance) {
        throw new Error('AppearanceContext not found');
    }
    return appearance;
};
